/**
 * Match Details screen constants.
 * Real odds come from the API (GET /matches/:matchId → odds.markets). When markets
 * are missing or empty, the screen shows no market sections (no mock odds).
 */

export const MATCH_DETAILS_ROUTE = '/app/create/match-details'

/** Max odds buttons per row in section cards; when more, render multiple rows. */
export const MAX_ODDS_PER_ROW = 3

export const MATCH_DETAILS_TABS = {
  MAIN: 'main',
  GOALS: 'goals',
  SCORES: 'scores',
  STATS: 'stats',
} as const

export type MatchDetailsTabKey = (typeof MATCH_DETAILS_TABS)[keyof typeof MATCH_DETAILS_TABS]

/** Market ids (align with API-Football where applicable) */
export const MARKET_IDS = {
  MATCH_WINNER: 1,
  DOUBLE_CHANCE: 2,
  BTTS: 3,
  SECOND_HALF_WINNER: 40,
  OVER_UNDER_15: 4,
  OVER_UNDER_25: 5,
  OVER_UNDER_35: 6,
  EXACT_GOALS: 7,
  ODD_EVEN: 41,
  HANDICAP_RESULT: 9,
  CORRECT_SCORE: 10,
  HALF_TIME_FULL_TIME: 11,
  CORNERS: 12,
  CARDS: 13,
  FIRST_HALF_WINNER: 42,
  HALF_TIME_CORRECT_SCORE: 14,
  OVER_UNDER_05_1ST_HALF: 15,
  OVER_UNDER_15_1ST_HALF: 16,
  OVER_UNDER_25_1ST_HALF: 17,
  OVER_UNDER_05_2ND_HALF: 18,
  OVER_UNDER_15_2ND_HALF: 19,
  OVER_UNDER_25_2ND_HALF: 20,
  CLEAN_SHEET_HOME: 27,
  CLEAN_SHEET_AWAY: 28,
  ASIAN_HANDICAP: 29,
  HIGHEST_SCORING_HALF: 30,
  /** European handicap by line: home handicap (negative = home disadvantage). Legacy picks use HANDICAP_RESULT (9). */
  HANDICAP_MINUS_4: 31,
  HANDICAP_MINUS_3: 32,
  HANDICAP_MINUS_2: 33,
  HANDICAP_MINUS_1: 34,
  HANDICAP_0: 35,
  HANDICAP_PLUS_1: 36,
  HANDICAP_PLUS_2: 37,
  HANDICAP_PLUS_3: 38,
  HANDICAP_PLUS_4: 39,
  BTTS_FIRST_HALF: 43,
  BTTS_SECOND_HALF: 44,
  WIN_BOTH_HALVES: 45,
} as const

/** Handicap line (integer) -> our market ID. Lines outside -4..+4 have no ID (API mapping drops them). */
export const HANDICAP_LINE_TO_MARKET_ID: Readonly<Record<number, number>> = {
  [-4]: 31,
  [-3]: 32,
  [-2]: 33,
  [-1]: 34,
  0: 35,
  1: 36,
  2: 37,
  3: 38,
  4: 39,
} as const

export const HANDICAP_MARKET_IDS = [
  MARKET_IDS.HANDICAP_MINUS_4,
  MARKET_IDS.HANDICAP_MINUS_3,
  MARKET_IDS.HANDICAP_MINUS_2,
  MARKET_IDS.HANDICAP_MINUS_1,
  MARKET_IDS.HANDICAP_0,
  MARKET_IDS.HANDICAP_PLUS_1,
  MARKET_IDS.HANDICAP_PLUS_2,
  MARKET_IDS.HANDICAP_PLUS_3,
  MARKET_IDS.HANDICAP_PLUS_4,
] as const

/** All handicap market IDs (legacy 9 + per-line 31–39). Use to enforce “at most one handicap pick per game”. */
export const HANDICAP_MARKET_IDS_ALL: readonly number[] = [
  MARKET_IDS.HANDICAP_RESULT,
  ...HANDICAP_MARKET_IDS,
]

/** Our handicap market ID → line (for display). */
export const HANDICAP_MARKET_ID_TO_LINE: Readonly<Record<number, number>> = {
  [MARKET_IDS.HANDICAP_MINUS_4]: -4,
  [MARKET_IDS.HANDICAP_MINUS_3]: -3,
  [MARKET_IDS.HANDICAP_MINUS_2]: -2,
  [MARKET_IDS.HANDICAP_MINUS_1]: -1,
  [MARKET_IDS.HANDICAP_0]: 0,
  [MARKET_IDS.HANDICAP_PLUS_1]: 1,
  [MARKET_IDS.HANDICAP_PLUS_2]: 2,
  [MARKET_IDS.HANDICAP_PLUS_3]: 3,
  [MARKET_IDS.HANDICAP_PLUS_4]: 4,
}

/** Parse legacy handicap value "Home -1", "Draw -2" → line number, or null. */
export function getHandicapLineFromLegacyValue(value: string | undefined): number | null {
  if (value == null || typeof value !== 'string') return null
  const m = value.trim().match(/([+-]?\d+)/)
  return m ? parseInt(m[1], 10) : null
}

/**
 * Line number for a handicap slip: from marketId (31–39) or from legacy value (market 9).
 */
export function getHandicapLineFromSlip(marketId: number, value?: string): number | null {
  const fromId = HANDICAP_MARKET_ID_TO_LINE[marketId]
  if (fromId !== undefined) return fromId
  if (marketId === MARKET_IDS.HANDICAP_RESULT) return getHandicapLineFromLegacyValue(value ?? '')
  return null
}

/** Canonical display base for all handicap markets so production and localhost show the same label. */
export const HANDICAP_DISPLAY_BASE_NAME = 'Handicap';

/**
 * Display name for handicap on challenge cards. Always uses "Handicap" + line so it's consistent
 * whether the stored marketName was "Handicap Result" (legacy/API) or "Handicap -1" (per-line).
 * Returns marketName unchanged when not a handicap market.
 */
export function formatHandicapMarketDisplayName(
  marketId: number,
  marketName: string,
  value?: string
): string {
  const line = getHandicapLineFromSlip(marketId, value)
  if (line === null) return marketName
  const suffix = line === 0 ? ' (0)' : line > 0 ? ` (+${line})` : ` (${line})`
  return `${HANDICAP_DISPLAY_BASE_NAME}${suffix}`
}

/**
 * Canonical list of supported markets (id + display name) in UI order.
 * Single source of truth for "these are the markets we support for now."
 */
export const SUPPORTED_MARKETS: ReadonlyArray<{ id: number; name: string }> = [
  { id: MARKET_IDS.MATCH_WINNER, name: 'Match Winner (1X2)' },
  { id: MARKET_IDS.DOUBLE_CHANCE, name: 'Double Chance' },
  { id: MARKET_IDS.BTTS, name: 'Both Teams To Score' },
  { id: MARKET_IDS.FIRST_HALF_WINNER, name: 'First Half Winner' },
  { id: MARKET_IDS.SECOND_HALF_WINNER, name: 'Second Half Winner' },
  { id: MARKET_IDS.OVER_UNDER_15, name: 'Over/Under 1.5' },
  { id: MARKET_IDS.OVER_UNDER_25, name: 'Over/Under 2.5' },
  { id: MARKET_IDS.OVER_UNDER_35, name: 'Over/Under 3.5' },
  { id: MARKET_IDS.ODD_EVEN, name: 'Odd/Even' },
  { id: MARKET_IDS.EXACT_GOALS, name: 'Exact Goals' },
  { id: MARKET_IDS.CORRECT_SCORE, name: 'Correct Score' },
  { id: MARKET_IDS.HALF_TIME_CORRECT_SCORE, name: 'Half Time Correct Score' },
  { id: MARKET_IDS.CORNERS, name: 'Corners Over/Under' },
  { id: MARKET_IDS.CARDS, name: 'Cards Over/Under' },
  { id: MARKET_IDS.CLEAN_SHEET_HOME, name: 'Clean Sheet - Home' },
  { id: MARKET_IDS.CLEAN_SHEET_AWAY, name: 'Clean Sheet - Away' },
  { id: MARKET_IDS.OVER_UNDER_05_1ST_HALF, name: 'First Half Over/Under 0.5' },
  { id: MARKET_IDS.OVER_UNDER_15_1ST_HALF, name: 'First Half Over/Under 1.5' },
  { id: MARKET_IDS.OVER_UNDER_25_1ST_HALF, name: 'First Half Over/Under 2.5' },
  { id: MARKET_IDS.HANDICAP_MINUS_4, name: 'Handicap -4' },
  { id: MARKET_IDS.HANDICAP_MINUS_3, name: 'Handicap -3' },
  { id: MARKET_IDS.HANDICAP_MINUS_2, name: 'Handicap -2' },
  { id: MARKET_IDS.HANDICAP_MINUS_1, name: 'Handicap -1' },
  { id: MARKET_IDS.HANDICAP_0, name: 'Handicap 0' },
  { id: MARKET_IDS.HANDICAP_PLUS_1, name: 'Handicap +1' },
  { id: MARKET_IDS.HANDICAP_PLUS_2, name: 'Handicap +2' },
  { id: MARKET_IDS.HANDICAP_PLUS_3, name: 'Handicap +3' },
  { id: MARKET_IDS.HANDICAP_PLUS_4, name: 'Handicap +4' },
  { id: MARKET_IDS.HALF_TIME_FULL_TIME, name: 'Half Time / Full Time' },
  { id: MARKET_IDS.HIGHEST_SCORING_HALF, name: 'Highest Scoring Half' },
  { id: MARKET_IDS.BTTS_FIRST_HALF, name: 'BTTS - First Half' },
  { id: MARKET_IDS.BTTS_SECOND_HALF, name: 'BTTS - Second Half' },
  { id: MARKET_IDS.WIN_BOTH_HALVES, name: 'Win Both Halves' },
  { id: MARKET_IDS.OVER_UNDER_05_2ND_HALF, name: 'Second Half Over/Under 0.5' },
  { id: MARKET_IDS.OVER_UNDER_15_2ND_HALF, name: 'Second Half Over/Under 1.5' },
  { id: MARKET_IDS.OVER_UNDER_25_2ND_HALF, name: 'Second Half Over/Under 2.5' },
]

/** Markets that use the score-picker UI (home/away +/- with odd change hints) */
export const SCORE_PICKER_MARKET_IDS: number[] = [
  MARKET_IDS.CORRECT_SCORE,
  MARKET_IDS.HALF_TIME_CORRECT_SCORE,
]

/** Over/Under market IDs – rendered as one table with Over|Under rows (goals, first half, second half, corners). At most one O/U pick per match. */
export const OVER_UNDER_MARKET_IDS: number[] = [
  MARKET_IDS.OVER_UNDER_15,
  MARKET_IDS.OVER_UNDER_25,
  MARKET_IDS.OVER_UNDER_35,
  MARKET_IDS.OVER_UNDER_05_1ST_HALF,
  MARKET_IDS.OVER_UNDER_15_1ST_HALF,
  MARKET_IDS.OVER_UNDER_25_1ST_HALF,
  MARKET_IDS.OVER_UNDER_05_2ND_HALF,
  MARKET_IDS.OVER_UNDER_15_2ND_HALF,
  MARKET_IDS.OVER_UNDER_25_2ND_HALF,
  MARKET_IDS.CORNERS,
]

/** Full-time goals Over/Under (1.5, 2.5, 3.5). */
export const FULL_TIME_OVER_UNDER_IDS: number[] = [
  MARKET_IDS.OVER_UNDER_15,
  MARKET_IDS.OVER_UNDER_25,
  MARKET_IDS.OVER_UNDER_35,
]

/** First-half goals Over/Under (0.5, 1.5, 2.5). */
export const FIRST_HALF_OVER_UNDER_IDS: number[] = [
  MARKET_IDS.OVER_UNDER_05_1ST_HALF,
  MARKET_IDS.OVER_UNDER_15_1ST_HALF,
  MARKET_IDS.OVER_UNDER_25_1ST_HALF,
]

/** Second-half goals Over/Under (0.5, 1.5, 2.5). */
export const SECOND_HALF_OVER_UNDER_IDS: number[] = [
  MARKET_IDS.OVER_UNDER_05_2ND_HALF,
  MARKET_IDS.OVER_UNDER_15_2ND_HALF,
  MARKET_IDS.OVER_UNDER_25_2ND_HALF,
]

/** O/U families for merging into one table (Goals 1.5/2.5/3.5, First Half, Second Half, Corners). */
export const OU_MARKET_FAMILIES: ReadonlyArray<{ ids: number[]; baseName: string }> = [
  { ids: [MARKET_IDS.OVER_UNDER_15, MARKET_IDS.OVER_UNDER_25, MARKET_IDS.OVER_UNDER_35], baseName: 'Goals Over/Under' },
  { ids: [MARKET_IDS.OVER_UNDER_05_1ST_HALF, MARKET_IDS.OVER_UNDER_15_1ST_HALF, MARKET_IDS.OVER_UNDER_25_1ST_HALF], baseName: 'First Half Over/Under' },
  { ids: [MARKET_IDS.OVER_UNDER_05_2ND_HALF, MARKET_IDS.OVER_UNDER_15_2ND_HALF, MARKET_IDS.OVER_UNDER_25_2ND_HALF], baseName: 'Second Half Over/Under' },
  { ids: [MARKET_IDS.CORNERS], baseName: 'Corners Over/Under' },
]

/** Handicap family: one section with a row per line (-4 to +4), each row Home/Draw/Away. */
export const HANDICAP_MARKET_FAMILY: { ids: readonly number[]; baseName: string } = {
  ids: HANDICAP_MARKET_IDS,
  baseName: 'Handicap',
}

/**
 * Desired market IDs per tab. Main = 1X2, Double Chance, BTTS. Goals = O/U, Exact Goals.
 * Scores = Correct Score, Half Time Correct Score. Stats = Corners, Clean Sheet Home, Clean Sheet Away.
 */
/**
 * Basketball market ids (specs/multi-sport-challenges).
 *
 * Must match `server/src/constants/odds.constants.js` BASKETBALL_MARKET_IDS. Reserved 100+ so
 * they can never collide with football's 1–45.
 */
export const BASKETBALL_MARKET_IDS = {
  MONEYLINE: 100,
  POINT_SPREAD: 101,
  TOTAL_POINTS: 102,
  FIRST_QUARTER_WINNER: 103,
  HIGHEST_SCORING_QUARTER: 104,
  /** Parity of the combined final score, overtime included. Format-independent. */
  ODD_EVEN: 105,
  /**
   * The result at the end of regulation, BEFORE overtime — basketball's primary market.
   *
   * Anything rendering this must say so. A bet labelled plain "Match Winner" that loses because
   * the team won in overtime is a trust problem, not a copy nit; the server names it
   * "Match Winner (Regulation)" for exactly that reason.
   */
  REGULATION_1X2: 106,
} as const;

/**
 * Formula One market ids (specs/formula-one-challenges).
 *
 * Must match `server/src/constants/odds.constants.js` FORMULA_ONE_MARKET_IDS. Reserved 200+ so
 * they can never collide with football's 1-45 or basketball's 100-104.
 *
 * All three are FIELD markets: the pick is a driver drawn from the race's entrants, so a value is
 * a driver's name rather than a side. Pole Position is deliberately absent — qualifying resolves
 * before the race starts, so it would be a bet on a known result.
 */
export const FORMULA_ONE_MARKET_IDS = {
  RACE_WINNER: 200,
  PODIUM_FINISH: 201,
  FASTEST_LAP: 202,
} as const;

export const TAB_MARKET_IDS: Record<MatchDetailsTabKey, number[]> = {
  [MATCH_DETAILS_TABS.MAIN]: [
    // Formula One (specs/formula-one-challenges). Same reasoning as basketball below: a fixture
    // only ever carries one sport's markets, so the tab never mixes them.
    FORMULA_ONE_MARKET_IDS.RACE_WINNER,
    FORMULA_ONE_MARKET_IDS.PODIUM_FINISH,
    FORMULA_ONE_MARKET_IDS.FASTEST_LAP,
    // Basketball (specs/multi-sport-challenges). Listed alongside football's rather than in their
    // own tab: a match only ever carries one sport's markets, so the tab never mixes them.
    BASKETBALL_MARKET_IDS.MONEYLINE,
    BASKETBALL_MARKET_IDS.POINT_SPREAD,
    BASKETBALL_MARKET_IDS.TOTAL_POINTS,
    MARKET_IDS.MATCH_WINNER,
    MARKET_IDS.DOUBLE_CHANCE,
    MARKET_IDS.BTTS,
    MARKET_IDS.BTTS_FIRST_HALF,
    MARKET_IDS.BTTS_SECOND_HALF,
    MARKET_IDS.WIN_BOTH_HALVES,
    MARKET_IDS.FIRST_HALF_WINNER,
    MARKET_IDS.SECOND_HALF_WINNER,
    MARKET_IDS.HANDICAP_RESULT, // legacy: odds synced before split may still have id 9
    ...HANDICAP_MARKET_IDS,
    MARKET_IDS.HALF_TIME_FULL_TIME,
  ],
  [MATCH_DETAILS_TABS.GOALS]: [
    MARKET_IDS.OVER_UNDER_15,
    MARKET_IDS.OVER_UNDER_25,
    MARKET_IDS.OVER_UNDER_35,
    MARKET_IDS.OVER_UNDER_05_1ST_HALF,
    MARKET_IDS.OVER_UNDER_15_1ST_HALF,
    MARKET_IDS.OVER_UNDER_25_1ST_HALF,
    MARKET_IDS.OVER_UNDER_05_2ND_HALF,
    MARKET_IDS.OVER_UNDER_15_2ND_HALF,
    MARKET_IDS.OVER_UNDER_25_2ND_HALF,
    MARKET_IDS.ODD_EVEN,
    MARKET_IDS.EXACT_GOALS,
    MARKET_IDS.HIGHEST_SCORING_HALF,
  ],
  [MATCH_DETAILS_TABS.SCORES]: [
    MARKET_IDS.CORRECT_SCORE,
    MARKET_IDS.HALF_TIME_CORRECT_SCORE,
  ],
  [MATCH_DETAILS_TABS.STATS]: [MARKET_IDS.CORNERS, MARKET_IDS.CARDS, MARKET_IDS.CLEAN_SHEET_HOME, MARKET_IDS.CLEAN_SHEET_AWAY],
}

/** Default badge count shown on match row when real count not available */
export const DEFAULT_AVAILABLE_BETS_BADGE = 42

/**
 * Set to true once RevenueCat subscriptions are live.
 * false = all markets unlocked, crowns hidden, no Pro gate everywhere.
 */
export const PREMIUM_MARKETS_ENABLED = false

/**
 * iOS native only: hide premium prediction types until App Store IAP is live.
 * Web/Android unchanged. Set false when iOS subscriptions ship.
 */
export const PREMIUM_MARKETS_IOS_HIDDEN_UNTIL_IAP = false

/**
 * Market IDs that require a Pro subscription — shown with a crown badge in the picker.
 * For merged-family chips (Goals O/U, Handicap) only some rows are premium;
 * use isChipFullyPremium() to decide whether to dim the whole chip.
 */
export const PREMIUM_MARKET_IDS: ReadonlySet<number> = new Set([
  // Half-time predictions
  MARKET_IDS.FIRST_HALF_WINNER,
  MARKET_IDS.SECOND_HALF_WINNER,
  MARKET_IDS.HALF_TIME_FULL_TIME,
  MARKET_IDS.HALF_TIME_CORRECT_SCORE,
  MARKET_IDS.BTTS_FIRST_HALF,
  MARKET_IDS.BTTS_SECOND_HALF,
  MARKET_IDS.WIN_BOTH_HALVES,
  MARKET_IDS.HIGHEST_SCORING_HALF,
  MARKET_IDS.OVER_UNDER_05_1ST_HALF,
  MARKET_IDS.OVER_UNDER_15_1ST_HALF,
  MARKET_IDS.OVER_UNDER_25_1ST_HALF,
  MARKET_IDS.OVER_UNDER_05_2ND_HALF,
  MARKET_IDS.OVER_UNDER_15_2ND_HALF,
  MARKET_IDS.OVER_UNDER_25_2ND_HALF,
  // Statistics
  MARKET_IDS.CORNERS,
  MARKET_IDS.CARDS,
  // Handicaps beyond -1/+1
  MARKET_IDS.HANDICAP_MINUS_4,
  MARKET_IDS.HANDICAP_MINUS_3,
  MARKET_IDS.HANDICAP_MINUS_2,
  MARKET_IDS.HANDICAP_0,
  MARKET_IDS.HANDICAP_PLUS_2,
  MARKET_IDS.HANDICAP_PLUS_3,
  MARKET_IDS.HANDICAP_PLUS_4,
  // Clean sheet
  MARKET_IDS.CLEAN_SHEET_HOME,
  MARKET_IDS.CLEAN_SHEET_AWAY,
  // Exact goals
  MARKET_IDS.EXACT_GOALS,
  // Over/Under goals — all except 2.5
  MARKET_IDS.OVER_UNDER_15,
  MARKET_IDS.OVER_UNDER_35,
])

/**
 * Returns true when every market in the chip's family is premium.
 * False for mixed families (Goals O/U has free 2.5; Handicap has free ±1)
 * — those chips are never dimmed; only their individual rows are.
 */
export function isChipFullyPremium(chipId: number): boolean {
  for (const family of OU_MARKET_FAMILIES) {
    if (family.ids[0] === chipId) {
      return (family.ids as readonly number[]).every((id) => PREMIUM_MARKET_IDS.has(id))
    }
  }
  if ((HANDICAP_MARKET_FAMILY.ids as readonly number[]).includes(chipId)) {
    return (HANDICAP_MARKET_FAMILY.ids as readonly number[]).every((id) => PREMIUM_MARKET_IDS.has(id))
  }
  return PREMIUM_MARKET_IDS.has(chipId)
}
